/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable class-methods-use-this */
import * as Services from '@services';
import {
  CreateVehicle,
  MotHistory,
  PagedResultDto,
  PagedUserVehicleDto,
  PagedVehicle,
  RefreshVehicleData,
  UpdateVehicle,
  UpdateVehicleMileage,
  UserVehicle,
  UserVehicleRequestDto,
  Vehicle,
  VehicleService,
} from '@services';
import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';

class VehicleStore {
  motHistory: MotHistory | undefined;
  vehicle: Vehicle | undefined;
  userVehicle: UserVehicle | undefined;

  userVehicles: PagedResultDto<UserVehicle> = {
    totalCount: 0,
    items: [],
  };
  loading = false;
  refreshLoading = false;
  selectVehicles: Map<string, Vehicle> = new Map();
  selectLoading = false;
  selectSkipCount = 0;
  selectPageSize = 10;
  selectTotalCount = 0;

  motHistoryLoading = false;
  averageMonthlyMileage = 0;

  constructor() {
    makeAutoObservable(this);
  }

  async getByRegistrationNumber(registrationNumber: string) {
    const result = await Services.VehicleService.getByRegistrationNumber(registrationNumber);
    runInAction(() => {
      this.vehicle = result;
    });
    return result;
  }

  async getByVin(vin: string) {
    const result = await Services.VehicleService.getByVin(vin);
    runInAction(() => {
      this.vehicle = result;
    });
    return result;
  }

  /** Orders from the BE can lack a VIN, so look up by it when there is one and by plate otherwise. */
  async getByVinOrRegistration(vin?: string | null, registrationNumber?: string | null) {
    if (vin) return this.getByVin(vin);
    if (registrationNumber) return this.getByRegistrationNumber(registrationNumber);
    return undefined;
  }

  /** The VIN on record for a plate, if there is one. Never fails, and leaves the shared `vehicle` alone. */
  async findVinByRegistration(registrationNumber: string): Promise<string | undefined> {
    try {
      const vehicle = await VehicleService.findByRegistrationNumber(registrationNumber);
      return vehicle?.combinedVin || undefined;
    } catch {
      return undefined;
    }
  }

  async getSelectVehicles(request: PagedVehicle) {
    this.setSelectLoading(true);
    try {
      const result = await VehicleService.getAll(request);
      runInAction(() => {
        result.items.forEach((vehicle: Vehicle) => {
          this.selectVehicles.set(vehicle.id, vehicle);
        });
        this.selectTotalCount = result.totalCount;
      });
      this.setSelectLoading(false);
    } catch (error) {
      this.setSelectLoading(false);
      console.error(error);
    }
  }

  async create(vehicle: CreateVehicle) {
    this.setLoading(true);
    try {
      const response = await VehicleService.create(vehicle);
      this.setLoading(false);
      return response;
    } catch (error) {
      this.setLoading(false);
      return null;
    }
  }

  async update(vehicle: UpdateVehicle) {
    this.setLoading(true);
    try {
      const response = await VehicleService.update(vehicle);
      runInAction(() => {
        this.vehicle = response;
      });

      this.setLoading(false);
      return response;
    } catch (error) {
      this.setLoading(false);
      return null;
    }
  }

  async refreshVehicleData(request: RefreshVehicleData) {
    this.setRefreshLoading(true);
    try {
      const response = await VehicleService.refreshVehicleData(request);
      runInAction(() => {
        this.vehicle = response;
      });
      message.success({
        content: 'Vehicle data refreshed successfully!',
      });
      this.setRefreshLoading(false);
      return response;
    } catch (error) {
      this.setRefreshLoading(false);
      return null;
    }
  }

  async updateVehicleMileage(vehicle: UpdateVehicleMileage) {
    this.setLoading(true);
    try {
      const response = await VehicleService.updateVehicleMileage(vehicle);
      runInAction(() => {
        this.vehicle = response;
      });

      this.setLoading(false);
      return response;
    } catch (error) {
      this.setLoading(false);
      return null;
    }
  }

  async checkVehicleMileage(vehicle: UpdateVehicleMileage) {
    this.setLoading(true);
    try {
      const response = await VehicleService.checkVehicleMileage(vehicle);
      this.setLoading(false);
      return response;
    } catch (error) {
      this.setLoading(false);
      return false;
    }
  }

  async createCustom(vehicle: CreateVehicle) {
    this.setLoading(true);
    try {
      const response = await VehicleService.createCustom(vehicle);
      this.setLoading(false);
      message.success({
        content: 'Successfully created!',
      });
      return response;
    } catch (error) {
      this.setLoading(false);
      return null;
    }
  }

  async getUserVehicles(request: PagedUserVehicleDto) {
    const result = await VehicleService.getUserVehicles(request);
    this.userVehicles = result;
  }

  async getUserVehicle(request: UserVehicleRequestDto) {
    const result = await VehicleService.getUserVehicle(request);
    this.userVehicle = result;
  }

  async getMotHistory(registrationNumber: string) {
    this.setMotHistoryLoading(true);
    try {
      const response = await VehicleService.getMotHistory(registrationNumber);
      this.setMotHistoryLoading(false);
      runInAction(() => {
        this.motHistory = response;

        if (!response?.motTests || response.motTests.length === 0) {
          this.averageMonthlyMileage = 0;
          return;
        }

        const lastTest = response?.motTests?.[0];
        const oneBeforeLast = response?.motTests?.[1];

        const averageMileageDifference =
          Number(lastTest?.odometerValue) - Number(oneBeforeLast?.odometerValue);

        this.averageMonthlyMileage = averageMileageDifference / 12;
      });
    } catch (error) {
      console.error(error);
      this.setMotHistoryLoading(false);
    }
  }

  async getMotHistoryByVin(vin: string) {
    this.setMotHistoryLoading(true);
    try {
      const response = await VehicleService.getMotHistoryByVin(vin);
      this.setMotHistoryLoading(false);
      runInAction(() => {
        this.motHistory = response;

        if (!response?.motTests || response.motTests.length === 0) {
          this.averageMonthlyMileage = 0;
          return;
        }

        const lastTest = response?.motTests?.[0];
        const oneBeforeLast = response?.motTests?.[1];

        const averageMileageDifference =
          Number(lastTest?.odometerValue) - Number(oneBeforeLast?.odometerValue);

        this.averageMonthlyMileage = averageMileageDifference / 12;
      });
    } catch (error) {
      console.error(error);
      this.setMotHistoryLoading(false);
    }
  }

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setRefreshLoading = (value: boolean) => {
    runInAction(() => {
      this.refreshLoading = value;
    });
  };

  setVehicle = (vehicle: Vehicle | undefined) => {
    runInAction(() => {
      this.vehicle = vehicle;
    });
  };

  setMotHistoryLoading = (value: boolean) => {
    runInAction(() => {
      this.motHistoryLoading = value;
    });
  };

  setSelectLoading = (value: boolean) => {
    runInAction(() => {
      this.selectLoading = value;
    });
  };

  clearVehicle = () => (this.vehicle = undefined);

  clearUserVehicles = () => {
    runInAction(() => {
      this.userVehicles.items = [];
      this.userVehicles.totalCount = 0;
    });
  };

  clearMotHistory = () => {
    runInAction(() => {
      this.motHistory = undefined;
    });
  };

  clearSelectVehicles = () => {
    runInAction(() => {
      this.selectVehicles.clear();
      this.selectTotalCount = 0;
    });
  };

  setSelectSkipCount = (value: number) => {
    runInAction(() => {
      this.selectSkipCount = value;
    });
  };

  resetSelectVehicles = () => {
    runInAction(() => {
      this.selectVehicles.clear();
      this.selectTotalCount = 0;
    });
  };

  resetUserVehicle = () => {
    runInAction(() => {
      this.userVehicle = undefined;
    });
  };
}

export default VehicleStore;
